import { inject, Injectable } from '@angular/core';
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  Messaging,
  onMessage,
} from 'firebase/messaging';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { cloudMessageMapper } from '../helpers';
import { Message } from '../models';

import { UserMeService } from './user-me.service';
import { CloudMessagingProvider } from '@app/core/models';
import { FIREBASE_APP } from '@app/core/tokens/firebase-app.token';

type ProviderMessageDataType = Omit<Message, 'references'> & {
  references: string;
};

@Injectable({
  providedIn: 'root',
})
export class WebCloudMessagingService implements CloudMessagingProvider {
  private firebaseApp = inject(FIREBASE_APP);

  private messages$ = new Subject<Message>();

  constructor(private userMeService: UserMeService) {}

  init() {
    isSupported().then((supported) => {
      if (!supported) {
        return;
      }

      const messaging = this.getMessaging();

      Notification.requestPermission()
        .then(() => this.getGrantedToken(messaging))
        .then((token) => this.setUserMeToken(token))
        .catch(() => null);

      onMessage(messaging, (providerMessage) => {
        const dataParsed =
          providerMessage.data as unknown as ProviderMessageDataType;

        this.messages$.next(
          cloudMessageMapper({
            ...dataParsed,
            references: JSON.parse(dataParsed.references),
            user_group_id: +dataParsed.user_group_id,
          }),
        );
      });
    });
  }

  destroy() {
    return from(isSupported()).pipe(
      switchMap((supported) => {
        if (!supported) {
          return of(null);
        }

        const messaging = this.getMessaging();

        return from(this.getGrantedToken(messaging)).pipe(
          switchMap((token) => {
            if (!token) {
              return of(null);
            }

            return this.userMeService.unsetNotificationToken(token).pipe(
              switchMap(() => from(deleteToken(messaging))),
              switchMap(() => of(null)),
            );
          }),
          catchError(() => {
            return of(null);
          }),
        );
      }),
    );
  }

  getMessages(): Observable<Message> {
    return this.messages$.asObservable();
  }

  // getMessaging() throws `messaging/unsupported-browser` where Service
  // Worker/Push are missing (e.g. iOS WKWebView): call only after isSupported().
  private getMessaging(): Messaging {
    return getMessaging(this.firebaseApp);
  }

  // Unlike compat, modular getToken() shows the permission prompt while it is
  // still 'default': never prompt from here (e.g. on logout).
  private getGrantedToken(messaging: Messaging): Promise<string | null> {
    if (Notification.permission !== 'granted') {
      return Promise.resolve(null);
    }

    return getToken(messaging);
  }

  private setUserMeToken(token: string) {
    if (!token) {
      return;
    }

    this.userMeService.setNotificationToken(token).subscribe();
  }
}
